import { Link } from "react-router-dom";
import MenuSheet from "@/components/MenuSheet";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ShieldCheck, Lock, Mail } from "lucide-react";

export default function PrivacyPolicyPage() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const updated = "June 10, 2026";
  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-3">
          {isAuthed && <MenuSheet />}
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="privacy-back-button">
            <Link to={isAuthed ? "/" : "/login"}>
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              {isAuthed ? "Back to planner" : "Back to sign in"}
            </Link>
          </Button>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <ShieldCheck strokeWidth={1.5} className="size-3.5" />
            Privacy
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Privacy policy
          </h1>
          <p className="text-sm text-muted-foreground">Last updated: {updated}</p>
        </div>

        <Card className="rounded-2xl">
          <CardContent className="pt-6 space-y-6 text-[15px] leading-relaxed">
            <section className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
              <Lock strokeWidth={1.5} className="size-5 text-primary shrink-0 mt-0.5" />
              <p className="m-0">
                <strong>The short version:</strong> your data is yours. We don't sell it, we don't trade it, and we don't share it with advertisers. Lumivera exists to help you, not to monetize you.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">1. What we collect</h2>
              <p className="m-0">When you use Lumivera, we collect only what's necessary to make the app work:</p>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li><strong>Account info:</strong> your name, email address, and a hashed (one-way encrypted) password.</li>
                <li><strong>Content you create:</strong> events, tasks, goals, journal entries, letters to future self, vision board images, custom theme preferences, today's focus, reviews.</li>
                <li><strong>Basic technical data:</strong> standard server logs (IP address, browser, timestamps) used for security and to keep the app running.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">2. What we don't do</h2>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li>We <strong>do not sell</strong> your data to anyone. Ever.</li>
                <li>We <strong>do not share</strong> your personal information with advertisers, data brokers, or third parties for marketing.</li>
                <li>We <strong>do not read</strong> your journal entries, letters, or vision boards. They're stored to serve back to you, not to analyze.</li>
                <li>We <strong>do not train</strong> AI models on your content.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">3. How we use your information</h2>
              <p className="m-0">Strictly to:</p>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li>Show you your own content across devices.</li>
                <li>Authenticate you securely when you sign in.</li>
                <li>Send you the notifications you've explicitly opted into (event reminders).</li>
                <li>Keep the service running, secure, and free of abuse.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">4. How your data is stored</h2>
              <p className="m-0">
                Your data lives in an encrypted MongoDB database hosted on managed cloud infrastructure. Passwords are hashed with bcrypt (a one-way function) — we cannot see them, even if we wanted to. Connections to the app use HTTPS.
              </p>
              <p className="m-0">
                Sealed letters to future self are protected at the API level: the content is not returned to the client until the lock date passes.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">5. Third-party services</h2>
              <p className="m-0">
                Lumivera runs on hosting infrastructure (Emergent / cloud platform) that processes data on our behalf under their own privacy commitments. We use no advertising networks, no analytics SDKs that profile individuals, and no third-party tracking.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">6. Your rights</h2>
              <p className="m-0">You can at any time:</p>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li>View, edit, or delete any content you've created inside the app.</li>
                <li>Request deletion of your entire account and all associated data by emailing us (see Contact).</li>
                <li>Export a copy of your data on request.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">7. Children</h2>
              <p className="m-0">
                Lumivera is not directed at children under 13 and we do not knowingly collect personal information from them. If you believe a child has provided us information, please contact us and we'll delete it.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">8. Changes to this policy</h2>
              <p className="m-0">
                If we update this policy meaningfully, we'll show a notice in the app. The "Last updated" date at the top of this page will always reflect the latest revision.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0 flex items-center gap-2">
                <Mail strokeWidth={1.5} className="size-5 text-primary" />
                9. Contact
              </h2>
              <p className="m-0">
                Questions, requests, or concerns? Email{" "}
                <a
                  href="mailto:amendozalp0220@gmail.com"
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  amendozalp0220@gmail.com
                </a>{" "}
                and we'll get back to you.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
